/* Single source of truth for every backend route the CMS talks to. */
const ENDPOINTS = {
  AUTH: {
    OTP_INITIATE: '/login/passwordless/Webinitiate',
    OTP_VERIFY: '/login/passwordless/verify',
    /* refresh_token → fresh access_token (no auth header needed) */
    REFRESH: '/access_token',
    LOGOUT: '/auth/logout',
  },
  CONTEXTS: '/contexts',
  /* Sign-up / onboarding. The parent profile PATCH is the same multipart route
     the phone app posts its onboarding to — it writes user_metadata, joins the
     chosen grade networks and flips login_step to "3" in one call. */
  USER: {
    AI_PARENT_PROFILE: '/user/ai-parent-profile',
    /* editing an account that's already set up — merges into user_metadata and
       leaves network membership and login_step alone */
    PARENT_PROFILE_UPDATE: '/user/parent-profile/update',
    ACTIVITIES: '/user/getActivity',
  },
  SCHOOL: {
    /* schools near a point, each with its grade-level class networks */
    CLASS_DATA: '/school/get-school-class-data',
  },
  CLASS_ADMIN: {
    CREATE_CLASS: '/classAdmin/createClassV2',
    /* every class group under one grade network, split into the ones this user
       has joined and the ones they haven't */
    CLASSES_BY_GRADE: (networkId) => `/classAdmin/getClassesV2/${networkId}`,
    JOIN_CLASS: '/classAdmin/joinClassV2',
    /* records the user as a room parent of a class group */
    SET_CLASS_ADMIN: '/classAdmin/areyouclassadmin',
  },
  EVENTS: {
    BASE: '/events',
    BY_ID: (id) => `/events/${id}`,
    SEND_INVITE: (id) => `/events/${id}/send-invite`,
  },
  PARENTS: {
    BASE: '/parents',
    BY_ID: (id) => `/parents/${id}`,
    ROOM_PARENT: (id) => `/parents/${id}/room-parent`,
  },
  LUNCH_MENU: '/lunch-menu',
  LINKS: {
    BASE: '/links',
    BY_ID: (id) => `/links/${id}`,
  },
  EMAILS: {
    SEND: '/emails/send',
    HISTORY: '/emails/history',
    REGENERATE: '/emails/regenerate',
  },
  ROOM_PARENTS: {
    BASE: '/room-parents',
    BY_NAME: (name) => `/room-parents/${encodeURIComponent(name)}`,
    ACTIVITY: '/room-parents/activity',
  },
  FUNDRAISING: {
    DONATIONS: '/fundraising/donations',
    EXPENSES: '/fundraising/expenses',
    EXPORT: '/fundraising/export',
  },
  /* The app's own class-links routes — same documents as ROOM_PARENT_ADMIN.LINK,
     same auth. The admin family has no delete, so the CMS uses this one. */
  GROUP_CHAT_LINKS: {
    DELETE: '/groupChatLinks/delete-group-chat-link',
  },
  ROOM_PARENT_ADMIN: {
    MY_PROFILE: '/roomParentAdmin/myProfile',
    UNSPLASH_PHOTOS: '/roomParentAdmin/unsplash/photos',
    GROUP_MEMBERS: (id) => `/roomParentAdmin/getGroupMember/${id}`,
    USER_EMAIL: '/roomParentAdmin/user/email',
    MEMBER_ROLE: '/roomParentAdmin/member/role',
    PARENT: '/roomParentAdmin/parent',
    PARENT_IMPORT: '/roomParentAdmin/parent/import',
    /* the route names this param :userId, but it deletes by parentId */
    PARENT_DELETE: (networkId, parentId) => `/roomParentAdmin/parent/${networkId}/${parentId}`,
    /* auth0 ids carry a "|" that has to survive the path */
    REMOVE_FROM_NETWORK: (networkId, userId) =>
      `/roomParentAdmin/removeFromNetwork/${networkId}/${encodeURIComponent(userId)}`,
    PARENTS_BY_NETWORK: (networkId) => `/roomParentAdmin/parents/${networkId}`,
    CONSENT_FORMS_BY_NETWORK: (networkId) => `/roomParentAdmin/consentForms/${networkId}`,
    DONATION: '/roomParentAdmin/donation',
    DONATIONS_BY_NETWORK: (networkId) => `/roomParentAdmin/donation/${networkId}`,
    /* The Zelle/Venmo/Apple Pay handles parents can pay to — the Links page's
       Donation Links, not the logged donations above. */
    DONATION_METHOD: '/roomParentAdmin/donationMethod',
    DONATION_METHODS_BY_NETWORK: (networkId) => `/roomParentAdmin/donationMethods/${networkId}`,
    /* Reminders: short class notes with an optional date and time. Delete is
       scoped to the network so an id from another class can't be removed. */
    REMINDER: '/roomParentAdmin/reminder',
    REMINDERS_BY_NETWORK: (networkId) => `/roomParentAdmin/reminders/${networkId}`,
    REMINDER_DELETE: (networkId, id) => `/roomParentAdmin/reminder/${networkId}/${id}`,
    EXPENSE: '/roomParentAdmin/expense',
    EXPENSES_BY_NETWORK: (networkId) => `/roomParentAdmin/expense/${networkId}`,
    EVENT_CREATE: '/roomParentAdmin/event/create',
    EVENT: '/roomParentAdmin/event',
    EVENT_STATUS: '/roomParentAdmin/event/status',
    EVENT_BY_ID: (id) => `/roomParentAdmin/event/${id}`,
    EVENTS_BY_NETWORK: (networkId) => `/roomParentAdmin/events/${networkId}`,
    /* a school's calendar: its events unioned with its NoSchool notes (days
       off), tagged itemType 'event' | 'note' and paged together. Master
       contexts only — a class or sub group lists plain events above. */
    EVENTS_WITH_NO_SCHOOL_BY_NETWORK: (networkId) => `/roomParentAdmin/event-with-todo/${networkId}`,
    LUNCH_MENU: (day) => `/roomParentAdmin/lunchMenu/${day}`,
    LINK: '/roomParentAdmin/link',
    LINK_STATUS: '/roomParentAdmin/link/status',
    LINKS_BY_NETWORK: (networkId) => `/roomParentAdmin/link/${networkId}`,
    /* Email templates: body copy + the field definitions its form renders. */
    EMAIL_TEMPLATE: '/roomParentAdmin/emailTemplate',
    EMAIL_TEMPLATES_BY_NETWORK: (networkId) => `/roomParentAdmin/emailTemplates/${networkId}`,
    EMAIL_TEMPLATE_BY_ID: (templateId) => `/roomParentAdmin/emailTemplate/${templateId}`,
    /* Gmail does the sending client-side; these record what went out. */
    SENT_EMAIL: '/roomParentAdmin/sentEmail',
    SENT_EMAILS_BY_NETWORK: (networkId) => `/roomParentAdmin/sentEmails/${networkId}`,
    SENT_EMAIL_BY_ID: (id) => `/roomParentAdmin/sentEmail/${id}`,
    /* one file per call, multipart field "file" + networkId; uploaded as it is
       picked so the sent-email record can carry a reference */
    EMAIL_ATTACHMENT: '/roomParentAdmin/emailAttachment',
    /* the teacher(s) copied on this class's emails — one row per address */
    TEACHER_EMAIL: '/roomParentAdmin/teacherEmail',
    TEACHER_EMAILS_BY_NETWORK: (networkId) => `/roomParentAdmin/teacherEmails/${networkId}`,
    TEACHER_EMAIL_BY_ID: (networkId, id) => `/roomParentAdmin/teacherEmail/${networkId}/${id}`,
    /* the class checklist. POST creates, PUT edits by todoId (partial). */
    TODO: '/roomParentAdmin/todo',
    /* DELETE — a soft delete, scoped to the network; 404 if the task isn't in it */
    TODO_BY_ID: (networkId, todoId) => `/roomParentAdmin/todo/${networkId}/${todoId}`,
    /* PATCH ticking a task off. Completion is recorded per user, so this takes
       { todoId } to mark it and { todoId, isDone: false } to take your own mark
       back off. */
    TODO_DONE: '/roomParentAdmin/todo/done',
    TODOS_BY_NETWORK: (networkId) => `/roomParentAdmin/todos/${networkId}`,
    /* sign-up flows are addressed by their generated signupId, not the Mongo _id */
    SIGNUP_FLOW: '/roomParentAdmin/signupFlow',
    SIGNUP_FLOW_STATUS: '/roomParentAdmin/signupFlow/status',
    SIGNUP_FLOW_BY_ID: (signupId) => `/roomParentAdmin/signupFlow/${signupId}`,
    SIGNUP_FLOWS_BY_NETWORK: (networkId) => `/roomParentAdmin/signupFlows/${networkId}`,
  },
  /* Class notes — the Sprout app's own calendar entries (event / Special /
     NoSchool / Todo), all POST. The CMS only writes the NoSchool kind: a
     Master Calendar day off, which every class under the school sees. */
  NOTE: {
    ADD: '/note/add-note',
    LIST: '/note/list-note',
    EDIT: '/note/edit-note',
    DELETE: '/note/delete-note',
  },
  PUBLIC: {
    SEARCH_ADDRESS: '/public/event/search-address',
  },
  IMAGES: {
    GENERATE: '/images/generate',
    UPLOAD: '/images/upload',
  },
};

export default ENDPOINTS;
